import React, { useCallback, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';

import AnswerPanelContext from 'components/customer/answer_panel/answer_panel_context';
import ClearAnswerSearch from 'actions/answers/clear_answer_search';
import CustomerContext from 'components/customer/customer_context';
import InsetContainer from 'components/common/containers/inset_container';
import SearchInput from 'components/common/search_input';
import SetSearchText from 'actions/answers/set_search_text';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export function AnswerSearchInput() {
  const { lastOpenedAt, searchText } = useContext(AnswerPanelContext);
  const inputRef = useRef(null);
  useFocusInput(inputRef, lastOpenedAt);

  const [onChange, onClear] = useHandleSearchTextChange();
  const onKeyDown = useOnKeyDown(inputRef);
  return (
    <InputWrapper>
      <SearchInput
        autoFocus={false}
        delay={SEARCH_DELAY}
        initialValue={searchText}
        onChange={onChange}
        onClearText={onClear}
        onKeyDown={onKeyDown}
        ref={inputRef}
      />
    </InputWrapper>
  );
}

const SEARCH_DELAY = 300; // ms
const InputWrapper = styled(InsetContainer).attrs({ inset: 'medium' })`
  padding-bottom: 0;
`;

const AnswerSearchInputMemo = React.memo(AnswerSearchInput);
export default AnswerSearchInputMemo;

function useHandleSearchTextChange() {
  const executeAction = useExecuteAction();
  const onChange = useCallback(evt => {
    const text = evt.target.value;
    executeAction(SetSearchText, { text });
  }, []);
  const onClear = useCallback(() => {
    executeAction(ClearAnswerSearch);
  }, []);
  return [onChange, onClear];
}

// Focus the input when openedAt changes and it occurs _after_ the customer was last routed at. Why?
// Because when we first mount a customer _and_ the answer panel is already open, we don't want to focus it..
// by checking to ensure that openedAt is after lastRoutedAt, we won't focus the answer panel on that
// first mount.
function useFocusInput(ref, openedAt) {
  const { lastRoutedAt } = useContext(CustomerContext);
  useEffect(() => {
    if (openedAt && new Date(openedAt) > new Date(lastRoutedAt)) {
      ref.current && ref.current.focusAll();
    }
  }, [openedAt, lastRoutedAt]);
}

// Blur the editor when we arrow up or down to notify agents that they have selected an
// answer in the list.
function useOnKeyDown(inputRef) {
  return useCallback(evt => {
    if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown') {
      inputRef.current.blur();
    }
  }, []);
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/answer_panel/search/answer_search_input.jsx